import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, delay, retry, shareReplay, take } from 'rxjs';
import { Profile } from 'src/models/profile';
import { Attendee } from 'src/models/attendee';
import { KeyValue } from '@angular/common';
import { Attendance } from 'src/models/attendance';
import { Service } from 'src/models/service';
import { KeyValueTable } from 'src/models/myKeyValue';
import { AttendeePicture } from 'src/models/attendeePicture';
import { VisitLog } from 'src/models/visitLog';
import { ServiceDocument } from 'src/models/serviceDocument';
import { ProfileAssignment } from 'src/models/profileAssignment'
import { User } from 'src/models/user';
import { SearchFilters } from 'src/models/searchFilters';
import { AppSettings } from '../app-settings';
import { Family } from 'src/models/family';
import { ModalController } from '@ionic/angular';
import { ProfileContainerComponent } from '../profile-container/profile-container.component';
import { Classroom } from 'src/models/classroom';
import { NotificationMessage } from 'src/models/notificationRequest';
import { WhatsappAddServiceRequest, WhatsappGroupRequest } from 'src/models/whatsappGroupRequest';
import { GroupPhoneNumber, ReferenceKeyType } from 'src/models/groupPhoneNumber';
import { WhatsappGroup } from 'src/models/whatsappGroup';

@Injectable({
    providedIn: 'root',
})
export class ProfileService {
    closeProfileEvent: EventEmitter<number> = new EventEmitter<number>();
    closeAddPersonEvent: EventEmitter<number> = new EventEmitter<number>();
    closeTransferPersonEvent: EventEmitter<number> = new EventEmitter<number>();
    profileUpdated: EventEmitter<number> = new EventEmitter<number>();
    familyIdEvent: EventEmitter<number> = new EventEmitter<number>();

    profileModal: any;

    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    };

    constructor(private http: HttpClient, private appSettings: AppSettings, private modalCtrl: ModalController) {
        this.closeProfileEvent.subscribe((value) => {
            this.profileModal.dismiss();
        });
    }

    async showProfile(profileId: number, familyId: number, isNewAppUser: boolean, isCoordinator: boolean = false, profileAssignmentId: number = 0) {
        this.profileModal = await this.modalCtrl.create({
            component: ProfileContainerComponent,
            componentProps: { profileId: profileId, familyId: familyId, isNewAppUser: isNewAppUser, isCoordinator: isCoordinator, profileAssignmentId },
            animated: true,
            initialBreakpoint: 1,
            breakpoints: [1],
            canDismiss: true,
            showBackdrop: true,
            backdropDismiss: true,
            keyboardClose: true,
        });

        this.profileModal.present();

        await this.profileModal.onWillDismiss();
    }

    getProfileByEmail(email: string): Observable<User[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetProfileByEmailNew?email=' + email;
        return this.http.get<User[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getProfileById(id: number, profileDataType: string): Observable<Profile> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetProfileByID?id=' + id + "&profileDataType=" + profileDataType;
        return this.http.get<Profile>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getFamilyById(id: number): Observable<Family> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetFamilyById?familyId=' + id;
        return this.http.get<Family>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getFamilies(id: number): Observable<Family[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetFamilies?familyId=' + id;
        return this.http.get<Family[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveFamily(family: Family): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveFamily';
        return this.http.post<boolean>(url, family, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getProfilesByDataType(id: number, profileDataType: string): Observable<Profile[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetProfilesByDataType?id=' + id + "&profileDataType=" + profileDataType;
        return this.http.get<Profile[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getMyAttendees(id: number | undefined, profileType: number): Observable<Attendee[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetMyAttendees?id=' + id + '&profileType=' + profileType;
        return this.http.get<Attendee[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getProfileAssignments(isServantsOnly: boolean): Observable<ProfileAssignment[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetProfileAssignments?isServantsOnly=' + isServantsOnly;
        return this.http.get<ProfileAssignment[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getProfilePhotosByIds(profileIds: string[]): Observable<Attendee[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetProfilesPicturesByIds?skipLoadingSpinner=1';
        return this.http.post<Attendee[]>(url, profileIds, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getServicesById(profileId: number): Observable<Service[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetServicesbyId?skipLoadingSpinner=1&profileId=' + profileId;
        return this.http.get<Service[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getPlanDatesByServiceId(serviceId: number, profileId = 0): Observable<KeyValue<string, Date>[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetPlanDatesByServiceId?serviceId=' + serviceId + '&profileId=' + profileId;
        return this.http.get<KeyValue<string, Date>[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getPlanAttendance(serviceId: number, selectedPlanDate: string, categoryId: number, profileTypeId: number = 1): Observable<Attendance[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetServiceAttendanceByDate?serviceId=' + serviceId +
            '&planDate=' + selectedPlanDate + '&categoryId=' + categoryId + '&profileTypeId=' + profileTypeId;

        return this.http.get<Attendance[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getMyAttendance(profileId: number, serviceId: number, selectedPlanDate: Date, year: number, month: number): Observable<Attendance[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetMyAttendance?profileId=' + profileId +
            '&serviceId=' + serviceId + '&year=' + year + '&month=' + month + '&planDate=' + selectedPlanDate;
        return this.http.get<Attendance[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    insertAttendance(planId: number, profileAssignmentIds: string, planDate: Date, userId: number | undefined, profileAssignmentIdsUnchecked: string): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/InsertAttendance?planId=' + planId +
            '&profileAssignmentIds=' + profileAssignmentIds +
            '&planDate=' + planDate.toLocaleDateString() + '&userId=' + userId +
            '&profileAssignmentIdsUnchecked=' + profileAssignmentIdsUnchecked;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveAttendance(profileAssignmentId: number, planDate: string, attendanceActualDateId: number): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveAttendance?profileAssignmentId=' + profileAssignmentId +
            '&planDate=' + planDate +
            '&attendanceActualDateId=' + attendanceActualDateId;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveAttendanceByQR(serviceId: number, planDate: Date): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveAttendanceByQR?serviceId=' + serviceId +
            '&planDate=' + planDate.toLocaleDateString();

        return this.http.get<number>(url, this.httpOptions)
            .pipe(retry({ count: 2, delay: 1500 }), shareReplay());
    }

    insertMyAttendance(profileAssignmentIds: string, profileAssignmentIdsUnchecked: string,
        planDate: Date, userId: number): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/InsertMyAttendance?planDate=' + planDate + '&userId='
            + userId + '&profileAssignmentIds=' + profileAssignmentIds +
            '&profileAssignmentIdsUnchecked=' + profileAssignmentIdsUnchecked;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    GetServiceDocuments(serviceId: number): Observable<ServiceDocument[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetServiceDocuments?serviceId=' + serviceId;
        return this.http.get<ServiceDocument[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getProfileDataTypes(): Observable<KeyValueTable[]> {
        let url = this.appSettings.apiBaseUrl() + 'Types/GetProfileDataTypes';
        return this.http.get<KeyValueTable[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveProfile(profile: Profile, userId: number, serviceId: number): Observable<Profile> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveProfile?userId=' + userId + '&serviceId=' + serviceId;
        return this.http.post<Profile>(url, profile, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    savePicture(profile: AttendeePicture): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SavePicture';
        return this.http.post<boolean>(url, profile, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getServiceLevels(): Observable<KeyValueTable[]> {
        let url = this.appSettings.apiBaseUrl() + 'Types/GetServiceLevels';
        return this.http.get<KeyValueTable[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getCoordinatorGroups(): Observable<KeyValueTable[]> {
        let url = this.appSettings.apiBaseUrl() + 'Types/GetCoordintorGroups';
        return this.http.get<KeyValueTable[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getVisitLogs(id: number, userId: number): Observable<VisitLog[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetVisitLogs?profileId=' + id + '&userId=' + userId;
        return this.http.get<VisitLog[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    insertVisitLog(newVisit: VisitLog): Observable<VisitLog> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/InsertVisitLog';
        return this.http.post<VisitLog>(url, newVisit, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    deleteVisit(visitId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/DeleteVisitById?id=' + visitId;
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    deleteProfile(profileId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/DeleteProfile?profileId=' + profileId;
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveProfileAssignment(profileId: number, serviceId: number, serviceLevelId: number, userId: number): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveProfileAssignment?profileId=' + profileId +
            "&serviceId=" + serviceId + "&serviceLevelId=" + serviceLevelId + "&userId=" + userId;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveProfileAssignments(profileId: number, serviceIds: string, serviceLevelId: number, userId: number): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveProfileAssignments?profileId=' + profileId +
            "&serviceIds=" + serviceIds + "&serviceLevelId=" + serviceLevelId + "&userId=" + userId;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveCoordinatorAssignment(profileId: number, serviceClassId: string, userId: number, serviceLevelId: number): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveCoordinatorAssignments?profileId=' + profileId +
            "&serviceClassIds=" + serviceClassId + "&userId=" + userId + '&serviceLevelId=' + serviceLevelId;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    saveServiceLevelAssignment(profileId: number, serviceLevelId: number): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SaveServiceLevel?profileId=' + profileId +
            "&serviceLevelId=" + serviceLevelId;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getUnassignedProfiles(serviceId: number): Observable<any[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetUnassignedProfiles?serviceId=' + serviceId;
        return this.http.get<any[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    assignProfileToService(profileId: number, serviceId: number, profileTypeId: number, serviceLevelId: number, userId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/AssignProfileToService?profileId=' + profileId + "&serviceId=" + serviceId +
            "&profileTypeId=" + profileTypeId + "&serviceLevelId=" + serviceLevelId + "&userId=" + userId;
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getAddressList(): Observable<any[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetAddressList';
        return this.http.get<any[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    validateFamilyJoinCode(joinCode: string): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/ValidateFamilyJoinCode?joinCode=' + joinCode;
        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    searchProfiles(searchFilters: SearchFilters): Observable<Profile[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/SearchProfiles';
        return this.http.post<Profile[]>(url, searchFilters, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    transferAttendeesToNewService(fromServiceId: number, toServiceId: number, profileAssignmentIds: string, userId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Profile/TransferAttendeeToNewService?fromServiceId=${fromServiceId}` +
            `&toServiceId=${toServiceId}&userId=${userId}&profileAssignmentIds=${profileAssignmentIds}`;
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getClassrooms(): Observable<Classroom[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetClassrooms';
        return this.http.get<Classroom[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getContactCard(profileId: number = 0, serviceId: number = 0): Observable<string> {
        let url = this.appSettings.apiBaseUrl() + `Profile/GetContactCard?profileId=${profileId}&serviceId=${serviceId}`;
        return this.http.get<string>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    removeProfileAssignments(profileId: number = 0): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Profile/RemoveProfileAssignments?profileId=${profileId}`;
        return this.http.get<boolean>(url, this.httpOptions);
    }

    getUserMessages(): Observable<NotificationMessage[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetUserNotifications'
        return this.http.get<NotificationMessage[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    getMyServices(profileId: number): Observable<Service[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetMyServices?profileId=' + profileId
        return this.http.get<Service[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 2, delay: 500 }), shareReplay());
    }

    manageEmailSubscription(email: string, isUnsubscribed): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Profile/ManageEmailSubscription?email=${email}&isUnsubscribe=${isUnsubscribed}`
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(retry({ count: 2, delay: 500 }), shareReplay());

    }

    isEmailSubscribed(email: string): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/IsEmailSubscribed?email=' + email
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(retry({ count: 2, delay: 500 }), shareReplay());
    }

    deleteProfileAssignment(profileAssignmentId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Profile/DeleteProfileAssignment?profileAssignmentId=${profileAssignmentId}`
        return this.http.delete<boolean>(url, this.httpOptions)
            .pipe(retry({ count: 2, delay: 500 }), shareReplay());
    }

    createWhatsappGroup(WhatsappGroupRequest: WhatsappGroupRequest): Observable<any> {
        let url = this.appSettings.apiBaseUrl() + 'Whatsapp/CreateGroup';
        return this.http.post<any>(url, WhatsappGroupRequest, this.httpOptions);
    }

    getPhoneNumbers(referenceKey: string, referenceKeyType: ReferenceKeyType): Observable<GroupPhoneNumber[]> {
        let url = this.appSettings.apiBaseUrl() + 'Whatsapp/GetGroupPhoneNumbers?referenceKey=' + referenceKey + '&referenceKeyType=' + referenceKeyType;
        return this.http.get<GroupPhoneNumber[]>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1500 }), shareReplay());
    }

    inviteToWhatsappGroup(profileId: number, eventId: number, serviceId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Whatsapp/InviteProfileToGroup?profileId=${profileId}&eventId=${eventId}&serviceId=${serviceId}`;
        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 3500 }), shareReplay());
    }

    getMyWhatsappGroups(): Observable<WhatsappGroup[]> {
        let url = this.appSettings.apiBaseUrl() + 'Whatsapp/GetMyWhatsappGroups';
        return this.http.get<WhatsappGroup[]>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1500 }), shareReplay());
    }

    deleteWhatsappGroup(groupId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Whatsapp/DeleteWhatsappGroup?groupId=' + groupId;
        return this.http.delete<boolean>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1500 }), shareReplay());
    }

    getWhatsappGroupReferenceKey(groupId: number): Observable<string> {
        let url = this.appSettings.apiBaseUrl() + 'Whatsapp/GetWhatsappGroupReferenceKey?groupId=' + groupId;
        return this.http.get<string>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1500 }), shareReplay());
    }

    getWhatsappGroupsByService(ServiceId: number): Observable<WhatsappGroup[]> {
        let url = this.appSettings.apiBaseUrl() + 'Whatsapp/GetMyWhatsappGroupsByService?serviceId=' + ServiceId;
        return this.http.get<WhatsappGroup[]>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1500 }), shareReplay());
    }

    addServiceToWhatsappGroup(request: WhatsappAddServiceRequest): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Whatsapp/AddServiceToGroup';
        return this.http.post<boolean>(url, request, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1500 }), shareReplay());
    }

    getAttendeesDatesTaken(serviceId?: number): Observable<Date[]> {
        let url = this.appSettings.apiBaseUrl() + 'Profile/GetAttendeesDatesTaken?serviceId=' + serviceId;
        return this.http.get<Date[]>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1500 }), shareReplay());

    }
}